/* eslint-disable camelcase */
import { SORT_ORDER, TFilterOptions } from '@evinced-private/ui-common';

import { preprocessSearchValue } from 'src/apps/mobile-common/helpers/MobileSearchHelper';
import { buildSeverityFilterVariables } from 'src/apps/mobile-common/helpers/MobileSeverityFilterHelper';
import { QueryVariablesBuilder } from 'src/common/helpers/GQLRequestsHelper/QueryVariablesBuilder';
import {
	ConditionOperators,
	Mfa_Session_RunsSortFields,
	Mfa_Session_RunsSortOrder,
	Mfa_Session_RunsTextSearchFields
} from 'src/common/types/graphql.generated';
import { ITableQueryParams } from 'src/common/types/TableQueryTypes';

import { MfaSessionsQueryVariables } from '../MfaSessions.generated';

const DAYS_AMOUNT_FILTER_PARAM_KEY = 'daysAmountFilter';

const sessionsTextSearchFields: Mfa_Session_RunsTextSearchFields[] = [
	Mfa_Session_RunsTextSearchFields.SessionName,
	Mfa_Session_RunsTextSearchFields.RunUpdatedAt,
	Mfa_Session_RunsTextSearchFields.CriticalIssues,
	Mfa_Session_RunsTextSearchFields.TotalIssues,
	Mfa_Session_RunsTextSearchFields.UserName,
	Mfa_Session_RunsTextSearchFields.App,
	Mfa_Session_RunsTextSearchFields.AppVersion,
	Mfa_Session_RunsTextSearchFields.Os,
	Mfa_Session_RunsTextSearchFields.CustomFields,
	Mfa_Session_RunsTextSearchFields.DeviceManufacturer,
	Mfa_Session_RunsTextSearchFields.DeviceModel,
	Mfa_Session_RunsTextSearchFields.DeviceType,
	Mfa_Session_RunsTextSearchFields.OsVersion,
	Mfa_Session_RunsTextSearchFields.AppBuildNumber,
	Mfa_Session_RunsTextSearchFields.ProductVersion
];

const sessionsSortFieldNamesMap: Record<string, Mfa_Session_RunsSortFields> = {
	sessionName: Mfa_Session_RunsSortFields.SessionName,
	runTime: Mfa_Session_RunsSortFields.RunUpdatedAt,
	lastModified: Mfa_Session_RunsSortFields.RunUpdatedAt,
	criticalIssues: Mfa_Session_RunsSortFields.CriticalIssues,
	totalIssues: Mfa_Session_RunsSortFields.TotalIssues,
	totalScans: Mfa_Session_RunsSortFields.TotalScans,
	createdBy: Mfa_Session_RunsSortFields.UserName,
	owner: Mfa_Session_RunsSortFields.UserName
};

const sessionsSortOrderMap: Record<SORT_ORDER, Mfa_Session_RunsSortOrder> = {
	[SORT_ORDER.ASC]: Mfa_Session_RunsSortOrder.Ascending,
	[SORT_ORDER.DESC]: Mfa_Session_RunsSortOrder.Descending
};

export const buildMfaSessionsQueryVariables = ({
	pageSize,
	page,
	sort,
	filterOptions
}: ITableQueryParams): MfaSessionsQueryVariables => {
	const variablesBuilder = new QueryVariablesBuilder<MfaSessionsQueryVariables>({
		sortFieldsMap: sessionsSortFieldNamesMap,
		sortOrderMap: sessionsSortOrderMap,
		searchFields: sessionsTextSearchFields
	});

	// Build core query parameters
	const searchValues = preprocessSearchValue(filterOptions?.searchValue ?? '');

	variablesBuilder
		.addPaginationParams(page, pageSize)
		.addSortParam(sort)
		.addTextSearchParam(searchValues);

	// Add dedicated filter fields
	if (filterOptions?.owner?.length) {
		variablesBuilder.addFilterParam('createdByFilter', filterOptions.owner);
	}

	if (filterOptions?.platform?.length) {
		variablesBuilder.addFilterParam('osFilter', filterOptions.platform);
	}

	if (filterOptions?.appName?.length) {
		variablesBuilder.addFilterParam('appFilter', filterOptions.appName);
	}

	if (filterOptions?.versions?.length) {
		variablesBuilder.addFilterParam('appVersionFilter', filterOptions.versions);
	}

	const knownFields = new Set([
		'owner',
		'sessionsPeriod',
		'searchValue',
		'platform',
		'appName',
		'versions',
		'severity'
	]);
	const customFields: TFilterOptions = {};

	if (filterOptions) {
		for (const [key, value] of Object.entries(filterOptions)) {
			if (!knownFields.has(key) && value && Array.isArray(value)) {
				customFields[key] = value;
			}
		}
	}

	// Handle custom fields if any exist
	if (Object.keys(customFields).length > 0) {
		variablesBuilder.addTupleFilterParam(
			'customFieldsFilter',
			customFields,
			ConditionOperators.All
		);
	}

	// Handle period filter
	const selectedPeriod = filterOptions?.sessionsPeriod?.[0];
	if (selectedPeriod?.value !== 'all') {
		variablesBuilder.addConditionParam(
			DAYS_AMOUNT_FILTER_PARAM_KEY,
			filterOptions.sessionsPeriod?.map((p) => Number(p.value))
		);
	}

	const variables = variablesBuilder.getVariables();
	const severityFilterVariables = buildSeverityFilterVariables<MfaSessionsQueryVariables>(
		filterOptions?.severity
	);

	return { ...variables, ...severityFilterVariables };
};
